import React, { useEffect, useState } from 'react'
import { View, Text, StyleSheet, Image, TouchableOpacity } from 'react-native'
import { Ionicons } from '@expo/vector-icons'
import StyledText from '../../../StyledText'
import theme from '../../../theme'
import CalculateDate from '../customUtils/calculateDates/calculateDates'
import { getImageStogeUrl } from '../imageUploader/ImageStorage'
import { getAvatarFromCache, setAvatarInCache } from '../../utils/ttlCache'
import { apiUrl } from '../../config/config'
// import { fetchAuthSession } from 'aws-amplify/auth'
import { getAuthToken } from '../../utils/functions.utils'
import styles from './ModalCommentsStyles'
import app from '../../../app.config'
import { avatarPlaceholder } from '../customUtils/Constants'
import { getLanguage } from '../appBar/context/LocalizationContext'
import { FlatList } from 'react-native-gesture-handler'
import { Platform } from 'react-native'
let BottomSheetFlatList
if (Platform.OS !== 'web') {
    const bottomSheet = require('@gorhom/bottom-sheet')
    BottomSheetFlatList = bottomSheet.BottomSheetFlatList
}

// Cache simple de URLs ya resueltas para Avatars en comentarios
const __commentsAvatarCache = new Map()

const CommentsComponent = ({
    comment,
    onReply,
    handleLike,
    inputRef,
    replies_response,
    comment_id,
}) => {
    const [showAllReplies, setShowAllReplies] = useState(
        comment_id ? comment._id.toString() === comment_id.toString() : false,
    )

    const [avatar, setAvatarUrl] = useState(avatarPlaceholder)
    const [page, setPage] = useState(0)
    const [size, setSize] = useState(2) // Paginación fija de a 2
    const [replies, setReplies] = useState([])

    // Una sola fuente de verdad para el total real que viene del servidor
    const [totalValue, setTotalValue] = useState(
        Number(comment?.replies_counter) || 0,
    )

    // Matemática pura: Total en servidor menos lo que ya rendericé en pantalla
    const totalRep = totalValue - replies.length
    const { languageFile } = getLanguage()

    // --- MANEJADORES DE INTERFACCIÓN ---

    const handleToggleReplies = async () => {
        if (replies.length === 0) {
            await fetchReplies(0)
        }
        setShowAllReplies(!showAllReplies)
    }

    const handleReply = (user_name, commentId) => {
        inputRef.current.focus()
        onReply(user_name, commentId)
    }

    const loadMoreReplies = () => {
        const nextPage = page + 1
        fetchReplies(nextPage)
        setPage(nextPage)
    }

    // --- PAGO DE DATOS (API) ---

    const fetchReplies = async (fetchPage) => {
        try {
            const authToken = await getAuthToken()
            if (!authToken) return

            const headers = {
                'Content-Type': 'application/json',
                Authorization: authToken,
                comment_id: comment._id,
            }

            if (String(comment._id).startsWith('temp_')) {
                throw new Error(
                    `[ModalComments] Cannot reply to optimistic comment: ${comment._id}`,
                )
            }

            const response = await fetch(
                `${apiUrl}/get_child_comments?page=${fetchPage}&size=${size}`,
                {
                    method: 'GET',
                    headers: headers,
                },
            )

            if (!response.ok) {
                const errorResponse = await response.json()
                throw new Error(
                    `Fallo al obtener comentarios. Status: ${response.status}`,
                )
            }

            const data = await response.json()

            if (data.status === 1) {
                const serverComments = data.single?.comments || []

                if (serverComments.length > 0) {
                    const updatedComments = await Promise.all(
                        serverComments.map(async (element) => {
                            const k = element.avatar_image
                            const hit = k ? __commentsAvatarCache.get(k) : null
                            if (hit) return { ...element, avatar_image: hit }
                            try {
                                const cached = await getAvatarFromCache(k)
                                if (cached) {
                                    __commentsAvatarCache.set(k, cached)
                                    return { ...element, avatar_image: cached }
                                }
                                const url = await getImageStogeUrl(k)
                                if (url) {
                                    __commentsAvatarCache.set(k, url)
                                    try {
                                        await setAvatarInCache(k, url)
                                    } catch {}
                                }
                                return { ...element, avatar_image: url || k }
                            } catch {
                                return { ...element }
                            }
                        }),
                    )

                    setReplies((prevState) => {
                        // Si se pide la página 0 (reset/nueva respuesta), limpiamos el array anterior
                        const baseReplies = fetchPage === 0 ? [] : prevState

                        const existingIds = new Set(
                            baseReplies.map((c) => c._id.toString()),
                        )
                        const uniqueNewComments = updatedComments.filter(
                            (c) => c?._id && !existingIds.has(c._id.toString()),
                        )
                        return [...baseReplies, ...uniqueNewComments]
                    })
                }

                // Sincronizamos el total real absoluto del backend
                const totalServerCount = data.single?.count || 0
                setTotalValue(totalServerCount)
            }
        } catch (error) {
            console.error('Error al obtener las respuestas:', error)
        }
    }

    // --- EFECTOS ---

    // Cache e inicialización del Avatar
    useEffect(() => {
        let mounted = true
        const key = comment?.avatar_image
        const hit = key ? __commentsAvatarCache.get(key) : null
        if (hit) {
            setAvatarUrl(hit)
            return
        }
        if (key) {
            getAvatarFromCache(key)
                .then((cached) => {
                    if (!mounted) return
                    if (cached) {
                        __commentsAvatarCache.set(key, cached)
                        setAvatarUrl(cached)
                        return true
                    }
                    return false
                })
                .then((servedFromCache) => {
                    if (servedFromCache) return
                    getImageStogeUrl(key)
                        .then(async (url) => {
                            if (!mounted) return
                            if (url) {
                                __commentsAvatarCache.set(key, url)
                                setAvatarUrl(url)
                                try {
                                    await setAvatarInCache(key, url)
                                } catch {}
                            }
                        })
                        .catch(() => {})
                })
                .catch(() => {})
        }
        return () => {
            mounted = false
        }
    }, [comment])

    // Escucha de nuevas respuestas (Real-time o Inyecciones del padre)
    useEffect(() => {
        if (replies_response && replies_response.length > 0) {
            const commentReplies = replies_response.filter(
                (rep) => rep?.comment_id?.toString() === comment._id.toString(),
            )

            if (!commentReplies || commentReplies.length === 0) return

            // Forzamos reseteo de página y traemos todo limpio desde el servidor
            setPage(0)
            fetchReplies(0)
        }
    }, [replies_response])

    // --- LÓGICA DE LIKES (Se mantiene intacta) ---
    const handleLikeComment = async (commentId, father_id) => {
        let rollback = null
        if (father_id && replies.length > 0) {
            setReplies((prev) => {
                let changed = false
                const next = prev.map((rep) => {
                    if (rep._id.toString() === commentId.toString()) {
                        const prevSel = !!rep.isSelected
                        const prevCount = Number(rep.likes_count) || 0
                        const nextSel = !prevSel
                        const nextCount = Math.max(0, prevCount + (nextSel ? 1 : -1))
                        rollback = {
                            _id: rep._id,
                            isSelected: prevSel,
                            likes_count: prevCount,
                        }
                        changed = true
                        return {
                            ...rep,
                            isSelected: nextSel,
                            likes_count: nextCount,
                        }
                    }
                    return rep
                })
                return changed ? next : prev
            })
        }

        let response_like = await handleLike(commentId, father_id)
        if (father_id && replies.length > 0 && response_like) {
            if (response_like.status === 1) {
                let dataResponse = response_like.single || {}
                const updatedReplies = replies.map((rep) => {
                    if (rep._id.toString() === dataResponse?.comment.toString()) {
                        return {
                            ...rep,
                            isSelected: dataResponse.status_like,
                            likes_count: dataResponse.counter,
                        }
                    }
                    return rep
                })
                setReplies(updatedReplies)
            } else if (rollback) {
                setReplies((prev) =>
                    prev.map((rep) =>
                        rep._id.toString() === rollback._id.toString()
                            ? {
                                  ...rep,
                                  isSelected: rollback.isSelected,
                                  likes_count: rollback.likes_count,
                              }
                            : rep,
                    ),
                )
            }
        }
    }

    return (
        <View style={{ marginBottom: 16 }}>
            <View style={[styles.comments]}>
                <View style={styles.avatarContainer}>
                    {(() => {
                        let avatarSource
                        if (typeof avatar === 'number') {
                            avatarSource = avatar
                        } else if (
                            avatar &&
                            typeof avatar === 'object' &&
                            typeof avatar.uri === 'string'
                        ) {
                            avatarSource = { uri: avatar.uri || avatarPlaceholder }
                        } else if (typeof avatar === 'string') {
                            avatarSource = { uri: avatar || avatarPlaceholder }
                        } else {
                            avatarSource = { uri: avatarPlaceholder }
                        }
                        return <Image style={styles.avatar} source={avatarSource} />
                    })()}
                </View>
                <View style={styles.commentsContent}>
                    <View style={styles.commentsBlockBottom}>
                        <View style={styles.commentHeader}>
                            <StyledText
                                fontWeight='bold'
                                fontSize='normalText'
                                flexShrink={1}
                            >
                                {comment.name
                                    ? comment?.name.length < 21 &&
                                      comment?.name !== comment?.name.toUpperCase()
                                        ? comment?.name
                                        : comment?.name ===
                                            comment?.name.toUpperCase()
                                          ? comment?.name.substring(0, 11) + '...'
                                          : comment?.name.substring(0, 20) + '...'
                                    : 'Usuario'}
                            </StyledText>
                            <StyledText fontSize='smallText' color='textSecondary'>
                                {CalculateDate(comment?.created_date)}
                            </StyledText>
                        </View>

                        <View>
                            <StyledText fontWeight='normal' fontSize='normalText'>
                                {comment?.comment}
                            </StyledText>
                        </View>

                        <View style={styles.commentFooter}>
                            {/* Usamos el totalValue dinámico en vez de la prop vieja estática */}
                            {totalValue > 0 && !showAllReplies && (
                                <TouchableOpacity
                                    onPress={() => handleToggleReplies()}
                                >
                                    <StyledText
                                        fontWeight='normal'
                                        fontSize='smallText'
                                        color='textSecondary'
                                    >
                                        {languageFile.see}
                                        {totalValue}
                                        {languageFile.answers}
                                    </StyledText>
                                </TouchableOpacity>
                            )}

                            <TouchableOpacity
                                onPress={() =>
                                    handleReply(
                                        `@${comment.name ?? ''} `,
                                        comment._id,
                                    )
                                }
                            >
                                <StyledText
                                    fontWeight='bold'
                                    fontSize='smallText'
                                    color={'textSecondary'}
                                >
                                    {languageFile.reply}
                                </StyledText>
                            </TouchableOpacity>
                        </View>
                    </View>

                    <View style={styles.commentsLikeCuore}>
                        <TouchableOpacity
                            onPress={() => handleLikeComment(comment._id, null)}
                            style={{ width: 25, alignItems: 'flex-end' }}
                        >
                            <View>
                                <Ionicons
                                    name={
                                        (comment ? comment.isSelected : false)
                                            ? 'heart-sharp'
                                            : 'heart-outline'
                                    }
                                    size={18}
                                    color={theme.colors.colorHeart}
                                    style={styles.iconPosts}
                                />
                                <View style={{ height: 4 }}></View>
                                <StyledText
                                    fontWeight='normal'
                                    fontSize='ultraSmallText'
                                    style={styles.likesCount}
                                >
                                    {comment?.likes_count}
                                </StyledText>
                            </View>
                        </TouchableOpacity>
                    </View>
                </View>
            </View>

            {/* Listado de respuestas */}
            {showAllReplies &&
                replies &&
                replies.map((reply, index) => (
                    <ReplysView
                        key={reply._id}
                        reply={reply}
                        handleLikeComment={(id) =>
                            handleLikeComment(id, comment._id)
                        }
                        isFirst={index === 0}
                        isLast={index === replies.length - 1}
                    />
                ))}

            {/* Lógica unificada para Footer de Paginación */}
            {totalRep > 0 && showAllReplies ? (
                <TouchableOpacity onPress={loadMoreReplies} style={[styles.see]}>
                    <StyledText
                        fontWeight='normal'
                        fontSize='smallText'
                        color={'textSecondary'}
                    >
                        {languageFile.see}
                        {totalRep} {languageFile.more_answers}
                    </StyledText>
                </TouchableOpacity>
            ) : (
                showAllReplies && (
                    <TouchableOpacity
                        onPress={handleToggleReplies}
                        style={styles.hide}
                    >
                        <StyledText
                            fontWeight='normal'
                            fontSize='smallText'
                            color={'textSecondary'}
                        >
                            {languageFile.hide}
                            {totalValue}
                            {languageFile.answers}
                        </StyledText>
                    </TouchableOpacity>
                )
            )}
        </View>
    )
}

export default CommentsComponent

export const ReplysView = ({ reply, handleLikeComment, isFirst, isLast }) => {
    const [avatar] = useState(reply?.avatar_image ?? avatarPlaceholder)

    return (
        <View style={[styles.reply, { marginTop: isFirst ? 10 : 0 }]}>
            <View style={styles.replyAvatarContainer}>
                {(() => {
                    let replySource
                    if (typeof avatar === 'number') {
                        replySource = avatar
                    } else if (
                        avatar &&
                        typeof avatar === 'object' &&
                        typeof avatar.uri === 'string'
                    ) {
                        replySource = { uri: avatar.uri || avatarPlaceholder }
                    } else if (typeof avatar === 'string') {
                        replySource = { uri: avatar || avatarPlaceholder }
                    } else {
                        replySource = { uri: avatarPlaceholder }
                    }
                    return <Image style={styles.replyAvatar} source={replySource} />
                })()}
            </View>
            <View style={[styles.replyContent, isLast && { marginBottom: 8 }]}>
                <View style={styles.commentHeader}>
                    <StyledText
                        fontWeight='bold'
                        fontSize='normalText'
                        color='textPrimary'
                    >
                        {reply?.name}
                    </StyledText>
                    <StyledText fontSize='smallText' color='textSecondary'>
                        {CalculateDate(reply?.created_date)}
                    </StyledText>
                </View>
                <StyledText
                    fontWeight='normal'
                    fontSize='normalText'
                    color='textPrimary'
                >
                    {reply?.comment}
                </StyledText>
            </View>

            <View style={styles.answerLikeCuore}>
                <TouchableOpacity
                    onPress={() => handleLikeComment(reply._id)}
                    style={{
                        width: 25,
                        alignItems: 'flex-end',
                    }}
                >
                    {/* <Ionicons name='heart-outline' size={20} color={theme.colors.colorTertiary} /> */}
                    <View>
                        <Ionicons
                            name={
                                (reply ? reply.isSelected : false)
                                    ? 'heart-sharp'
                                    : 'heart-outline'
                            }
                            size={18}
                            color={
                                (reply ? reply.isSelected : false)
                                    ? theme.colors.colorHeart
                                    : theme.colors.colorHeart
                            }
                            style={styles.iconPosts}
                        />
                        <View style={{ height: 4 }}></View>
                        <StyledText
                            fontWeight='normal'
                            fontSize='ultraSmallText'
                            style={styles.likesCount}
                        >
                            {reply?.likes_count}
                        </StyledText>
                    </View>
                </TouchableOpacity>
            </View>
        </View>
    )
}
